// LogoCropDialog.tsx — crop step of the workspace logo upload flow (workspace-logo cycle,
// 2026-08-21, spec §A). `react-easy-crop` handles drag/zoom over the picked image; "Use this
// logo" bakes the crop into a fixed 256×256 canvas (WebP, PNG fallback) client-side — the
// server never resizes (spec: "the canvas output is what is stored").
import { useEffect, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { useT } from '../locales/context';

const OUTPUT_SIZE = 256;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Could not load the selected image')));
    image.src = src;
  });
}

/** Bakes `croppedAreaPixels` (react-easy-crop's pixel-space crop rect, in source-image
 * coordinates) into a fixed `OUTPUT_SIZE` square canvas. WebP first (what the server stores
 * verbatim); PNG fallback covers the case where `canvas.toBlob('image/webp')` yields null (spec
 * §A). NOT unit-tested: happy-dom has no canvas/`drawImage`/`toBlob`/`Image` decode
 * implementation, so exercising this needs a real browser — kept as a standalone export
 * precisely so the dialog component around it stays thin and testable up to this boundary. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export async function getCroppedBlob(imageSrc: string, croppedAreaPixels: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser');
  // The crop box may extend past the image (zoom-out below 1 with `restrictPosition={false}`,
  // so a whole square logo can sit inside the selection with transparent margins — Johnny,
  // 2026-08-21, matching the sibling product). Rather than relying on every browser clipping an
  // out-of-bounds SOURCE rect correctly, place the whole image onto the output at the crop's
  // scale and let the canvas clip it: pixels outside the image stay transparent.
  const scale = OUTPUT_SIZE / croppedAreaPixels.width;
  ctx.drawImage(
    image,
    -croppedAreaPixels.x * scale,
    -croppedAreaPixels.y * scale,
    image.naturalWidth * scale,
    image.naturalHeight * scale,
  );
  const webp = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  if (webp) return webp;
  const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!png) throw new Error('Could not encode the cropped image');
  return png;
}

export function LogoCropDialog({
  file,
  onClose,
  onCropped,
}: {
  file: File | null;
  onClose: () => void;
  onCropped: (blob: Blob) => void;
}) {
  const t = useT();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Object URL creation is synced off `file` identity via React's "adjusting state during
  // rendering" pattern (guarded setState in the render body — same idiom WorkspaceSettings.tsx's
  // `syncedKey` uses, see that file's comment) rather than a `useEffect`, which
  // `eslint-plugin-react-hooks`'s `set-state-in-effect` rule flags as a cascading-render risk.
  const [syncedFile, setSyncedFile] = useState<File | null>(null);
  if (file !== syncedFile) {
    setSyncedFile(file);
    setImageSrc(file ? URL.createObjectURL(file) : null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
  }

  // Revoking the PREVIOUS object URL is a genuine effect (an external-resource cleanup, not a
  // state update) so it stays in `useEffect`, keyed on `imageSrc` so the URL just replaced —
  // or the one live when the dialog unmounts — is the one revoked, never the current one.
  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [imageSrc]);

  async function handleUse() {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsCropping(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      onCropped(blob);
      onClose();
    } catch {
      setError(t.workspace.cropErrorFallback);
    } finally {
      setIsCropping(false);
    }
  }

  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.workspace.cropDialogTitle}</DialogTitle>
          <DialogDescription>{t.workspace.cropDialogDescription}</DialogDescription>
        </DialogHeader>
        {imageSrc && (
          <div className="relative h-72 w-full overflow-hidden rounded-lg bg-muted">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              // Default `contain` + zoom-out allowed (`minZoom` 0.5, no position clamp): at zoom 1
              // the whole image is visible, and the user can shrink it further so a square logo
              // fits entirely inside the selection with transparent margins, as in LopHoc.
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              restrictPosition={false}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
        )}
        <div className="flex items-center gap-3">
          <span className="text-[12.5px] text-muted-foreground">{t.workspace.zoomLabel}</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[var(--primary)]"
            aria-label={t.workspace.zoomLabel}
          />
        </div>
        {error && <p className="text-[13px] text-destructive">{error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.workspace.cancelCta}
          </Button>
          <Button type="button" onClick={handleUse} disabled={!croppedAreaPixels || isCropping}>
            {t.workspace.useThisLogoCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
